/**
 * ResponsiveImage — drop-in <img> replacement that serves modern formats
 * and responsive widths for backend-served uploads.
 *
 * For any /api/files/* source the backend has WebP/AVIF variants at 480w,
 * 960w and 1600w.  The rendered markup uses <picture> so the browser can
 * pick the smallest supported format at the resolution it actually needs.
 *
 * External URLs and static seed assets (/assets/coach-photos/*) fall
 * through to a plain <img>.
 *
 * P1F — opt-in `deferUntilVisible`: when true (and NOT a priority image) the
 * real src/srcset is withheld until an IntersectionObserver reports the
 * element approaching the viewport. This stops proven below-fold homepage
 * media from starting its network fetch during the initial mobile LCP window.
 * `priority` images (the LCP / above-the-fold set) are never gated. The alt
 * attribute, className and any data-testid stay on the placeholder <img> so
 * layout (aspect container reserves the box), a11y and crawlability hold.
 */
import React, { useEffect, useRef, useState } from "react";
import { buildSrcSet, defaultSrc } from "@/lib/imageOptim";

const ResponsiveImage = React.forwardRef(function ResponsiveImage(
  {
    src,
    alt = "",
    sizes,
    maxWidth,
    className,
    priority = false,
    loading,       // explicit override — thumbnails render immediately
                   // even though they are not "priority" images
    deferUntilVisible = false,
    rootMargin = "300px",
    onError,
    ...rest
  },
  ref,
) {
  const innerRef = useRef(null);
  const setRefs = (node) => {
    innerRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  // priority images are the LCP set — never gate them.
  const gated = deferUntilVisible && !priority;
  const [visible, setVisible] = useState(!gated);

  useEffect(() => {
    if (!gated || visible) return;
    const el = innerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { rootMargin });
    io.observe(el);
    return () => io.disconnect();
  }, [gated, visible, rootMargin]);

  const srcSet = buildSrcSet(src, { maxWidth });
  const resolvedLoading = loading || (priority ? "eager" : "lazy");
  const commonImgProps = {
    ref: setRefs,
    alt,
    className,
    onError,
    loading: resolvedLoading,
    // React uses camelCase for these HTML attributes.
    fetchPriority: priority ? "high" : "auto",
    decoding: "async",
    ...rest,
  };

  if (gated && !visible) {
    // Reserve the box, keep alt + className + testids, but emit NO src/srcset
    // so no network request starts until the element approaches the viewport.
    const { onLoad, ...placeholderRest } = rest;
    return (
      <img
        ref={setRefs}
        alt={alt}
        className={className}
        loading={resolvedLoading}
        decoding="async"
        {...placeholderRest}
      />
    );
  }

  if (!srcSet) {
    // Non-optimizable — static assets, external URLs, placeholders.
    return <img src={src} {...commonImgProps} />;
  }
  return (
    <img
      src={defaultSrc(src)}
      srcSet={srcSet}
      sizes={sizes || "(max-width: 640px) 100vw, 960px"}
      {...commonImgProps}
    />
  );
});

export default ResponsiveImage;
